import {
    getPopularityData,
    getPopularityGoogleTimeSeriesMonthly,
    getPopularityInstagramTimeSeriesMonthly,
    getPopularityTikTokTimeSeriesMonthly,
} from '../api/popularity.api';
import { pluralDimensionToSingle, shapeTimeSeries } from './sourceDataShaping';
import createSourceDataHook from './createSourceDataHook';
import { getPopularityHealthStats } from '../../state/utils';
import { indexEntities } from 'utils/entities';

const shapeData = (data, price) => {
    if (!data) return null;
    return {
        ...data,
        cluster: data.current_cluster,
        healthStats: getPopularityHealthStats(data, price),
    };
};

const shapeOverviewTimeSeries = (timeSeries) => {
    if (!timeSeries) return null;
    return shapeTimeSeries(
        timeSeries
            .filter(
                ({ prediction }, idx, ref) =>
                    prediction || ref[idx + 1]?.prediction,
            )
            .map(({ prediction: _, ...rest }) => ({ ...rest })),
    );
};

const shapeCombinedTimeSeries = (timeSeries) => {
    if (!timeSeries) return null;
    return shapeTimeSeries(
        timeSeries
            .filter(({ prediction }) => !prediction)
            .map(({ prediction: _, ...rest }) => ({ ...rest })),
    );
};

const shapeFullOverviewTimeSeries = (timeSeries) => {
    if (!timeSeries) return null;
    return shapeTimeSeries(timeSeries.map((e) => ({ ...e })));
};

/** Exported for tests: the id fan-out is where the empty-page bug lived. */
export const queryFn = async ({ queryKey }) => {
    const {
        paginateBy,
        week,
        year,
        order_by,
        ascending,
        country,
        category,
        category_descendants,
        brand,
        per_page,
        page,
        withPricePoint,
    } = queryKey[1];
    const data = await getPopularityData({
        data: {
            category,
            category_descendants,
            brand,
            country,
            week,
            year,
            order_by,
            ascending,
            per_page,
            page,
            include_entities: true,
        },
    });
    const paginateIds =
        data?.data?.map(
            (record) => record[pluralDimensionToSingle[paginateBy]],
        ) || [];

    // Nothing to hydrate. The follow-up calls below spread the ids over the
    // same key the filter uses -- `category: paginateIds` -- so an empty page
    // would send `category: []`, which the endpoint rejects as a missing
    // filter. The request 400s, the query errors, and the view can no longer
    // tell "no rows" from "request failed".
    if (!paginateIds.length) return { ...data, data: [] };

    const handleData = (resp) =>
        resp?.data?.reduce((acc, record) => {
            acc[record[pluralDimensionToSingle[paginateBy]]] = record;
            return acc;
        }, {});
    const [
        timeSeriesOverviewById,
        timeSeriesGoogleById,
        timeSeriesTikTokById,
        timeSeriesInstagramById,
    ] = await Promise.all([
        getPopularityData({
            data: {
                category,
                brand,
                [pluralDimensionToSingle[paginateBy]]: paginateIds,
                country,
                order_by,
                ascending,
                per_page,
            },
        }).then(handleData),
        getPopularityGoogleTimeSeriesMonthly({
            data: {
                category,
                brand,
                [pluralDimensionToSingle[paginateBy]]: paginateIds,
                country,
                week,
                year,
                per_page,
            },
        }).then(handleData),
        getPopularityTikTokTimeSeriesMonthly({
            data: {
                category,
                brand,
                [pluralDimensionToSingle[paginateBy]]: paginateIds,
                country,
                week,
                year,
                per_page,
            },
        }).then(handleData),
        getPopularityInstagramTimeSeriesMonthly({
            data: {
                category,
                brand,
                [pluralDimensionToSingle[paginateBy]]: paginateIds,
                country,
                week,
                year,
                per_page,
            },
        }).then(handleData),
    ]);

    const items = indexEntities(data?.entities);

    const dataWithTimeSeriesAndItems = data?.data?.map((record) => {
        const id = record[pluralDimensionToSingle[paginateBy]];
        const { time_series: _, data, ...restRecord } = record;
        const paginateItem =
            items?.[id] ||
            (record.brand_name
                ? {
                      id,
                      name: record.brand_name,
                      type: pluralDimensionToSingle[paginateBy],
                  }
                : undefined);
        // brand_price rides the row, so it survives an unresolved sidecar id.
        const shapedData = shapeData(
            {
                ...data,
                week: record.week,
                year: record.year,
            },
            withPricePoint
                ? (restRecord.brand_price ?? paginateItem?.price)
                : undefined,
        );
        const timeSeriesGoogle = shapeTimeSeries(
            timeSeriesGoogleById?.[id]?.time_series,
        );
        const timeSeriesTikTok = shapeTimeSeries(
            timeSeriesTikTokById?.[id]?.time_series,
        );
        const timeSeriesInstagram = shapeTimeSeries(
            timeSeriesInstagramById?.[id]?.time_series,
        );
        const timeSeriesOverview = shapeOverviewTimeSeries(
            timeSeriesOverviewById?.[id]?.time_series,
        );
        const timeSeriesCombined = shapeCombinedTimeSeries(
            timeSeriesOverviewById?.[id]?.time_series,
        );
        const timeSeriesFullOverview = shapeFullOverviewTimeSeries(
            timeSeriesOverviewById?.[id]?.time_series,
        );
        return {
            ...restRecord,
            ...paginateItem,
            ...shapedData,
            searchField: paginateItem?.name,
            paginateItem,
            data: shapedData,
            timeSeriesOverview,
            timeSeriesGoogle,
            timeSeriesTikTok,
            timeSeriesInstagram,
            timeSeriesCombined,
            timeSeriesFullOverview,
            id,
            dataPointId: record.id,
        };
    });

    return { ...data, data: dataWithTimeSeriesAndItems };
};

const deduplicateData = (data) => {
    if (!(data && data.length)) return data;
    const seen = new Set();
    return data.filter(({ paginateItem }) => {
        if (seen.has(paginateItem?.id)) return false;
        if (paginateItem?.id) seen.add(paginateItem.id);
        return true;
    });
};

const usePopularity = createSourceDataHook({
    queryName: 'PopularityData',
    queryFn,
    defaults: {
        order_by: 'popularity_index_avg_12_month',
        week: '*',
        year: '*',
    },
    mapRows: deduplicateData,
    // The pop-index pages are stitched from three sources, so a transient
    // failure on a later page is worth more attempts than the default.
    restRetry: 5,
    skeletonRowIds: false,
});

export default usePopularity;
